<template>
  <b-row class="base-row-padded">
    <b-col>
      <b-button v-if="currentSuggestion" variant="light" @click="cancelProcess" class="mb-3">&lt; Cancel Batch
        Processing</b-button>

      <div v-if="currentSuggestion">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h3>Processing Group</h3>
          <div class="d-flex align-items-center">
            <b-button size="sm" variant="outline-primary" @click="prevPage" :disabled="currentIndex === 0"
              class="mr-2">Previous</b-button>
            <span class="text-muted mr-2">Suggestion {{ currentIndex + 1 }} of {{ suggestions.length }}</span>
            <b-button size="sm" variant="outline-primary" @click="nextPage"
              :disabled="currentIndex === suggestions.length - 1">Next</b-button>
          </div>
        </div>

        <b-card header="Group Details" class="mb-4">
          <b-form-group label="Group Name">
            <b-form-input v-model="currentSuggestion.name"></b-form-input>
          </b-form-group>
        </b-card>

        <b-card header="Devices to Add" class="mb-4">
          <b-table v-if="currentSuggestion.devices && currentSuggestion.devices.length"
            :items="currentSuggestion.devices" :fields="deviceFields" selectable select-mode="multi"
            @row-selected="onRowSelected" ref="devicesTable" responsive>
            <template #cell(selected)="{ rowSelected }">
              <b-form-checkbox :checked="rowSelected" disabled></b-form-checkbox>
            </template>
            <template #cell(name)="data">
              <router-link :to="{ name: 'showDevice', params: { id: data.item.id } }" target="_blank">{{ data.item.name
              }}</router-link>
            </template>
          </b-table>
          <div v-else class="text-muted">No devices in this group.</div>
        </b-card>

        <div class="d-flex justify-content-between">
          <b-button variant="danger" @click="skipGroup">Skip this Group</b-button>
          <div>
            <b-button variant="success" class="mr-2" :disabled="formLoading || selectedDevices.length === 0"
              @click="createNewGroup">Create New Group</b-button>
            <b-button variant="primary" :disabled="formLoading || selectedDevices.length === 0"
              @click="openExistingGroupModal">Add to Existing Group</b-button>
          </div>
        </div>
      </div>
      <div v-else>
        <b-alert show variant="success">All groups processed!</b-alert>
        <b-button variant="primary" :to="{ name: 'groups' }">Back to Groups</b-button>
      </div>

      <!-- Modal for selecting existing group -->
      <b-modal id="select-group-modal" title="Select Existing Group" hide-footer size="lg">
        <GroupsViewer :picker-mode="true" @item-clicked="onGroupPicked" />
      </b-modal>

    </b-col>
  </b-row>
</template>

<script>
import groupsMixin from './groupsMixin';
import formsMixin from '@/mixins/forms';
import settingsMixin from '@/mixins/settings';
import GroupsViewer from '@/components/GroupsViewer.vue';

export default {
  mixins: [groupsMixin, formsMixin, settingsMixin],
  components: { GroupsViewer },
  data() {
    return {
      suggestions: [],
      selectedDevices: [],
      currentIndex: 0,
      isSyncing: false,
      deviceFields: [
        { key: 'selected', label: '' },
        { key: 'name', label: 'Name' },
        { key: 'activation_code', label: 'Activation Code' },
        { key: 'address', label: 'Address' }
      ]
    }
  },
  computed: {
    currentSuggestion() {
      return this.suggestions.length > 0 ? this.suggestions[this.currentIndex] : null;
    }
  },
  watch: {
    currentIndex() {
      this.syncSelection();
    }
  },
  created() {
    this.loadState();
  },
  methods: {
    loadState() {
      const stored = sessionStorage.getItem('group_excel_suggestions');
      if (stored) {
        this.suggestions = JSON.parse(stored);
        // Initialize default selection if not present
        let modified = false;
        this.suggestions.forEach(suggestion => {
          if (!suggestion.selectedDeviceIds) {
            suggestion.selectedDeviceIds = suggestion.devices.map(d => d.id);
            modified = true;
          }
        });
        if (modified) {
          this.saveState();
        }
        this.syncSelection();
      } else {
        this.$router.push({ name: 'groups' });
      }
    },
    saveState() {
      if (this.suggestions.length > 0) {
        sessionStorage.setItem('group_excel_suggestions', JSON.stringify(this.suggestions));
      } else {
        sessionStorage.removeItem('group_excel_suggestions');
      }
    },
    syncSelection() {
      this.isSyncing = true;
      // Manually set selectedDevices so buttons update immediately and correctly
      const idsToSelect = this.currentSuggestion.selectedDeviceIds || [];
      this.selectedDevices = this.currentSuggestion.devices.filter(d => idsToSelect.includes(d.id));

      this.$nextTick(() => {
        if (this.$refs.devicesTable && this.currentSuggestion) {

          // Clear current selection first
          this.$refs.devicesTable.clearSelected();

          // Select rows
          this.currentSuggestion.devices.forEach((device, index) => {
            if (idsToSelect.includes(device.id)) {
              this.$refs.devicesTable.selectRow(index);
            }
          });

          // Unblock after events have propagated
          this.$nextTick(() => {
            this.isSyncing = false;
          });
        } else {
          this.isSyncing = false;
        }
      });
    },
    onRowSelected(items) {
      if (this.isSyncing) return;

      this.selectedDevices = items;
      // Persist selection for current item
      if (this.currentSuggestion) {
        this.currentSuggestion.selectedDeviceIds = items.map(d => d.id);
        this.saveState();
      }
    },
    prevPage() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
      }
    },
    nextPage() {
      if (this.currentIndex < this.suggestions.length - 1) {
        this.currentIndex++;
      }
    },
    async createNewGroup() {
      if (!this.currentSuggestion) return;
      const groupName = this.currentSuggestion.name;
      const deviceIds = this.selectedDevices.map(d => d.id);

      // 1. Create empty group
      try {
        const newGroup = await this.createGroup({
          name: groupName,
          settings: this.generateEmptySettings(),
          devices: [],
        }, true); // suppressRedirect = true

        // 2. Update with devices
        const groupToUpdate = { ...newGroup };
        groupToUpdate.devices = deviceIds;
        // fix the broken backend return
        if (!groupToUpdate.settings.business_hours) {
          groupToUpdate.settings.business_hours = {};
        }

        await this.updateGroup(groupToUpdate);

        this.finishCurrentStep();

      } catch (e) {
        // eslint-disable-next-line
        console.error(e);
      }
    },
    openExistingGroupModal() {
      this.$bvModal.show('select-group-modal');
    },
    async onGroupPicked(group) {
      // close the modal
      this.$bvModal.hide('select-group-modal');
      try {
        // 1. Fetch group
        const fullGroup = await this.getGroup({ id: group.id }, true);

        // 2. Add devices
        const currentDeviceIds = fullGroup && fullGroup.devices ? fullGroup.devices.map(d => d.id) : [];
        const newDeviceIds = this.selectedDevices.map(d => d.id);

        // Merge unique
        const allDeviceIds = [...new Set([...currentDeviceIds, ...newDeviceIds])];

        const groupToUpdate = { ...fullGroup };
        groupToUpdate.devices = allDeviceIds;

        // fix the broken backend return for business_hours if needed
        if (!groupToUpdate.settings.business_hours) {
          groupToUpdate.settings.business_hours = {};
        }
        await this.updateGroup(groupToUpdate);

        this.finishCurrentStep();
      } catch (e) {
        // eslint-disable-next-line
        console.error(e);
      }
    },
    finishCurrentStep() {
      // Remove current item
      this.suggestions.splice(this.currentIndex, 1);
      this.saveState();

      // Adjust index if we removed the last item
      if (this.currentIndex >= this.suggestions.length) {
        this.currentIndex = Math.max(0, this.suggestions.length - 1);
      }

      // If we still have items, sync selection for the new item at current index
      if (this.suggestions.length > 0) {
        this.syncSelection();
      }
    },
    skipGroup() {
      this.finishCurrentStep();
    },
    cancelProcess() {
      sessionStorage.removeItem('group_excel_suggestions');
      this.$router.push({ name: 'groupExcelUpload' });
    }
  }
}
</script>
